
import { checkStatus, generateBasicAuthHeaderValue, fetchWithTimeout } from './utils'

const baseAPI_URL = process.env.REACT_APP_BASE_API_URL
const dummyAPI_Token = '_' // since Taffy doesn't support optional tokens
const defaultInit = {
  credentials: 'include', // pass cookies
  headers: {'content-type': 'application/json'}
}

/**
 * Roundabout way of confirming the session is in good standing
 * on server. If CFID / CFTOKEN cookies are valid then this will
 * succeed.
 */
export const checkSessionStatus = () => {
  return getDecisions('en')
}

export const getDecisions = lang => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/decisions/${lang}`
    fetchWithTimeout(url, defaultInit)
      .then(checkStatus)
      .then(res => res.json())
      .then(resolve)
      .catch(reject)
  })
}

export const getDecisionResult = (sessionId) => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/result/${sessionId}`
    fetchWithTimeout(url, defaultInit)
      .then(checkStatus)
      .then(res => res.json())
      .then(resolve)
      .catch(reject)
  })
}

export const getOrganizationInfo = organizationId => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/organization/${organizationId}`
    fetchWithTimeout(url, defaultInit)
      .then(checkStatus)
      .then(res => res.json())
      .then(resolve)
      .catch(reject)
  })
}

export const initializeSession = (decisionId, caseId, lang) => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/session/${dummyAPI_Token}`
    const init = {
      ...defaultInit,
      method: 'POST',
      body: JSON.stringify({decisionId, caseId, languageCode: lang})
    }
    fetchWithTimeout(url, init)
      .then(checkStatus)
      .then(res => res.json())
      .then(resolve)
      .catch(reject)
  })
}

export const generatePdfReport = (sessionId, lang) => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/print/${sessionId}/${lang}`
    fetchWithTimeout(url, defaultInit)
      .then(checkStatus)
      .then(res => res.json())
      .then(resolve)
      .catch(reject)
  })
}

export const getMatchingSessionIDs = sessionId => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/sessionscase/${sessionId}`
    fetchWithTimeout(url, defaultInit)
      .then(checkStatus)
      .then(res => res.json())
      .then(resolve)
      .catch(reject)
  })
}

export const login = (username, password) => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/authenticate`
    const init = {
      ...defaultInit,
      headers: {
        ...defaultInit.headers,
        authorization: generateBasicAuthHeaderValue(username, password)
      }
    }
    fetchWithTimeout(url, init)
      .then(checkStatus)
      .then(res => res.json())
      .then(parsedResponse => {
        // check 200 response for value
        if(parsedResponse.AUTHORIZED) {
          resolve({
            givenName: parsedResponse.GIVENNAME,
            organizationId: parsedResponse.ORGANIZATIONID,
            roles: parsedResponse.ROLES,
            surname: parsedResponse.SURNAME
          })
        } else {
          reject(parsedResponse.ERROR)
        }
      })
      .catch(reject)
  })
}

export const logout = () => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/logout`
    fetchWithTimeout(url, defaultInit)
      .then(checkStatus)
      .then(resolve)
      .catch(reject)
  })
}

export const recallSession = sessionId => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/session/${sessionId}`
    fetchWithTimeout(url, defaultInit)
      .then(checkStatus)
      .then(res => res.json())
      .then(resolve)
      .catch(reject)
  })
}

export const saveSession = (serializedSession, sessionId) => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/session/${sessionId}`
    const init = {
      ...defaultInit,
      method: 'PUT',
      body: serializedSession
    }
    fetchWithTimeout(url, init)
      .then(checkStatus)
      // this response body is empty, but want to resolve with something other than undefined
      .then(() => resolve(true))
      .catch(reject)
  })
}

export const sendReportEmail = (recipients, content, sessionId, lang) => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/email/${sessionId}/${lang}`
    const init = {
      ...defaultInit,
      method: 'POST',
      body: JSON.stringify({'emailAddresses': recipients, 'emailBody': content})
    }
    fetchWithTimeout(url, init)
      .then(checkStatus)
      .then(resolve)
      .catch(reject)
  })
}

// Case Id uniqueness constraint is per decision, per user.
export const validateCaseId = (decisionId, caseId) => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/decisionsession/${decisionId}/${caseId}`
    fetchWithTimeout(url, defaultInit)
      .then(res => res.json())
      .then(parsedResponse => {
        if(parsedResponse.isUnique) {
          resolve(caseId)
        } else {
          reject()
        }
      })
      .catch(reject)
  })
}

const transformError = error => `${error}`

export const logError = (sendEmail, error, info) => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/error/`

    const errorMessage = {
      sendEmail: sendEmail,
      errorMessage: transformError(error, info)
    }

    const opts = {
      ...defaultInit,
      method: 'POST',
      body: JSON.stringify(errorMessage)
    }

    fetchWithTimeout(url, opts)
      .then(checkStatus)
      .then(resolve)
      .catch(reject)
  });
}

export const getSessionsByDecisionId = decisionId => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/sessionsdecision/${decisionId}`
    fetchWithTimeout(url, defaultInit)
      .then(checkStatus)
      .then(res => res.json())
      .then(resolve)
      .catch(reject)
  })
}

export const getSupportedLanguages = () => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/languages/`
    fetchWithTimeout(url, defaultInit)
      .then(checkStatus)
      .then(res => res.json())
      .then(resolve)
      .catch(reject)
  })
}

export const getSessionTranslation = (sessionId, languageCode) => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/sessionlanguage/${sessionId}/${languageCode}`
    fetchWithTimeout(url, defaultInit)
      .then(checkStatus)
      .then(res => res.json())
      .then(resolve)
      .catch(reject)
  })
}

export const addSessionTranslation = (sessionId, languageCode, translations) => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/sessionlanguage/${sessionId}/${languageCode}`
    const init = {
      ...defaultInit,
      method: 'POST',
      body: JSON.stringify(translations)
    }
    fetchWithTimeout(url, init)
      .then(checkStatus)
      .then(resolve)
      .catch(reject)
  })
}

export const setSessionTranslation = (sessionId, languageCode, translations) => {
  return new Promise((resolve, reject) => {
    const url = `${baseAPI_URL}/sessionlanguage/${sessionId}/${languageCode}`
    const init = {
      ...defaultInit,
      method: 'PUT',
      body: JSON.stringify(translations)
    }
    fetchWithTimeout(url, init)
      .then(checkStatus)
      .then(resolve)
      .catch(reject)
  })
}


// WEBPACK FOOTER //
// ./src/api/index.js